// Shared brand marks.
//
// VeilLogo / VeilLogoTile carry the VeilBrowser identity so every surface
// renders the same mark instead of a generic package icon. Cloudflare / Let's
// Encrypt are third-party marks (simple-icons paths, CC0) rendered in
// currentColor so they sit quietly next to lucide icons in pickers and status
// rows.
//
// The two Veil marks share one V. Only the figure/ground inverts: the
// standalone mark draws the V in the emerald gradient on whatever surface it
// sits on, and the tile knocks the V out of an emerald field. Geometry is
// duplicated in apps/web/src/app/icon.svg (favicon) and
// apps/local-api/src/shell/mark.ts (non-React surfaces); those two are the same
// mark, not variants, and move together with this file.
//
// All components accept className and size like lucide icons
// (`<VeilLogo className="size-4" />`).

import { useId } from "react";

interface BrandIconProps {
	className?: string;
	title?: string;
}

/** Near-white with an emerald tint, matching the signal-emerald-ink token. */
const INK = "#F5FCF8";

const GRADIENT_STOPS = (
	<>
		<stop offset="0%" stopColor="#34D399" />
		<stop offset="50%" stopColor="#10B981" />
		<stop offset="100%" stopColor="#047857" />
	</>
);

// Standalone mark: drawn large, since there is no tile edge to clear.
const MARK_V = "M13.5 17 L32 48 L50.5 17";
const MARK_ECHO = "M3 8 L32 60 L61 8";

// Tile: the same V, inset so it clears both the tile edge and the central 80%
// safe zone that Android maskable icons are cropped to. The echoes are wider
// chevrons of the same shape, so the layering reads as the veil falling behind
// the mark rather than as unrelated decoration. The outer one runs past the
// viewBox on purpose and is cut by the tile clip.
const TILE_V = "M17.5 19.5 L32 45.5 L46.5 19.5";
const TILE_ECHO_NEAR = "M7 10 L32 54 L57 10";
const TILE_ECHO_FAR = "M-2 2 L32 62 L66 2";

/** The VeilBrowser V mark alone (transparent background, emerald gradient). */
export function VeilLogo({ className, title = "VeilBrowser" }: BrandIconProps) {
	const id = useId();
	return (
		<svg
			viewBox="0 0 64 64"
			fill="none"
			xmlns="http://www.w3.org/2000/svg"
			className={className}
			role="img"
			aria-label={title}
		>
			<title>{title}</title>
			<defs>
				<linearGradient id={id} x1="0" y1="0" x2="1" y2="1">
					{GRADIENT_STOPS}
				</linearGradient>
			</defs>
			<path
				d={MARK_ECHO}
				stroke={`url(#${id})`}
				strokeWidth="2.2"
				fill="none"
				opacity="0.3"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d={MARK_V}
				fill="none"
				stroke={`url(#${id})`}
				strokeWidth="8.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</svg>
	);
}

/**
 * Emerald tile with the V knocked out. For login screens, brand slots, app
 * icons: anywhere the mark needs its own container.
 */
export function VeilLogoTile({
	className,
	title = "VeilBrowser",
}: BrandIconProps) {
	const id = useId();
	const gradientId = `${id}-fill`;
	const clipId = `${id}-clip`;
	return (
		<svg
			viewBox="0 0 64 64"
			fill="none"
			xmlns="http://www.w3.org/2000/svg"
			className={className}
			role="img"
			aria-label={title}
		>
			<title>{title}</title>
			<defs>
				<linearGradient id={gradientId} x1="0" y1="0" x2="1" y2="1">
					{GRADIENT_STOPS}
				</linearGradient>
				<clipPath id={clipId}>
					<rect width="64" height="64" rx="14" />
				</clipPath>
			</defs>
			<g clipPath={`url(#${clipId})`}>
				<rect width="64" height="64" fill={`url(#${gradientId})`} />
				<path
					d={TILE_ECHO_FAR}
					stroke={INK}
					strokeWidth="2"
					fill="none"
					opacity="0.11"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<path
					d={TILE_ECHO_NEAR}
					stroke={INK}
					strokeWidth="2.5"
					fill="none"
					opacity="0.2"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<path
					d={TILE_V}
					fill="none"
					stroke={INK}
					strokeWidth="7"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<rect
					x="0.5"
					y="0.5"
					width="63"
					height="63"
					rx="13.5"
					fill="none"
					stroke={INK}
					strokeWidth="1"
					opacity="0.14"
				/>
			</g>
		</svg>
	);
}

/** Cloudflare mark (simple-icons), rendered in currentColor. */
export function CloudflareIcon({
	className,
	title = "Cloudflare",
}: BrandIconProps) {
	return (
		<svg
			viewBox="0 0 24 24"
			xmlns="http://www.w3.org/2000/svg"
			fill="currentColor"
			className={className}
			role="img"
			aria-label={title}
		>
			<title>{title}</title>
			<path d="M16.5088 16.8447c.1475-.5068.0908-.9707-.1553-1.3154-.2246-.3164-.6045-.499-1.0615-.5205l-8.6592-.1123a.1559.1559 0 0 1-.1333-.0713c-.0283-.042-.0351-.0986-.021-.1553.0278-.084.1123-.1484.2036-.1562l8.7359-.1123c1.0351-.0489 2.1601-.8868 2.5537-1.9136l.499-1.3013c.0215-.0561.0293-.1128.0147-.168-.5625-2.5463-2.835-4.4453-5.5499-4.4453-2.5039 0-4.6284 1.6177-5.3876 3.8614-.4927-.3658-1.1187-.5625-1.794-.499-1.2026.119-2.1665 1.083-2.2861 2.2856-.0283.31-.0069.6128.0635.894C1.5683 13.171 0 14.7754 0 16.752c0 .1748.0142.3515.0352.5273.0141.083.0844.1475.1689.1475h15.9814c.0909 0 .1758-.0645.2032-.1553l.12-.4268zm2.7568-5.5634c-.0771 0-.1611 0-.2383.0112-.0566 0-.1054.0415-.127.0976l-.3378 1.1744c-.1475.5068-.0918.9707.1543 1.3164.2256.3164.6055.498 1.0625.5195l1.8437.1133c.0557 0 .1055.0263.1329.0703.0283.043.0351.1074.0214.1562-.0283.084-.1132.1485-.204.1553l-1.921.1123c-1.041.0488-2.1582.8867-2.5527 1.914l-.1406.3585c-.0283.0713.0215.1416.0986.1416h6.5977c.0771 0 .1474-.0489.169-.126.1122-.4082.1757-.837.1757-1.2803 0-2.6025-2.125-4.727-4.7344-4.727" />
		</svg>
	);
}

/** Let's Encrypt mark (simple-icons), rendered in currentColor. */
export function LetsEncryptIcon({
	className,
	title = "Let's Encrypt",
}: BrandIconProps) {
	return (
		<svg
			viewBox="0 0 24 24"
			xmlns="http://www.w3.org/2000/svg"
			fill="currentColor"
			className={className}
			role="img"
			aria-label={title}
		>
			<title>{title}</title>
			<path d="M11.9914 0a.8829.8829 0 00-.8718.817v3.0209A.8829.8829 0 0012 4.7207a.8829.8829 0 00.8803-.8803V.817a.8829.8829 0 00-.889-.817zm7.7048 3.1089a.8804.8804 0 00-.5214.1742l-2.374 1.9482a.8804.8804 0 00.5592 1.5622.8794.8794 0 00.5592-.2001l2.3714-1.9506a.8804.8804 0 00-.5944-1.534zm-15.3763.0133a.8829.8829 0 00-.611 1.5206l2.37 1.9506a.876.876 0 00.5606.2001v-.002a.8804.8804 0 00.5597-1.5602L4.8277 3.2831a.8829.8829 0 00-.5078-.161zm7.6598 3.2275a5.0456 5.0456 0 00-5.0262 5.0455v1.4876H5.787a.9672.9672 0 00-.9647.9643v9.1887a.9672.9672 0 00.9647.9643H18.213a.9672.9672 0 00.9643-.9643v-9.1907a.9672.9672 0 00-.9643-.9623h-1.1684v-1.4876a5.0456 5.0456 0 00-5.0649-5.0455zm.0127 2.8933a2.1522 2.1522 0 012.1593 2.1522v1.4876H9.8473v-1.4876a2.1522 2.1522 0 012.145-2.1522zm7.3812.5033a.8829.8829 0 10.0705 1.7632h3.0267a.8829.8829 0 000-1.7609H19.444a.8829.8829 0 00-.0705-.0023zm-17.8444.0023a.8829.8829 0 000 1.7609h2.9983a.8829.8829 0 000-1.7609zm10.4596 6.7746a1.2792 1.2792 0 01.641 2.3926v1.2453a.6298.6298 0 01-1.2595 0v-1.2453a1.2792 1.2792 0 01.6185-2.3926z" />
		</svg>
	);
}
